import { colors } from '@apollo/brand';
import { customColors } from '@apollo/orbit';
import { useMemo } from 'react';

import { HeatmapStackConfig } from 'src/components/charts/Heatmap';
import { HistogramBucket } from 'src/components/charts/helpers/histogram';
import { useStackInfo } from 'src/components/charts/helpers/useStackInfo';
import { decodeSparseBuckets } from 'src/lib/decodeSparseBuckets';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';

type QueryHeatMapStackData = {
  timestamp: Timestamp['output'];
  metrics: {
    cachedHistogram: { sparseBuckets: number[] };
    uncachedHistogram: { sparseBuckets: number[] };
  };
};

export function useServiceReportHeatmaps(
  requestsChartData: GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats[],
  eventsChartData: GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats[],
  isSubscriptionOperation: boolean,
) {
  const requestsHeatmapStacks = useMemo(
    (): MinLengthArray<2, HeatmapStackConfig<QueryHeatMapStackData>> => [
      {
        primaryColor: colors.primitives.blue[300],
        legendColor: colors.primitives.blue[300],
        data: requestsChartData,
        getValue: (stats: QueryHeatMapStackData) => ({
          timestamp: stats.timestamp,
          buckets: decodeSparseBuckets(
            stats.metrics.uncachedHistogram.sparseBuckets,
          ),
        }),
        hasData: (stats: QueryHeatMapStackData) =>
          stats.metrics.uncachedHistogram.sparseBuckets.some((v) => v > 0),
        label: isSubscriptionOperation ? 'Registrations' : 'Requests',
        unit: '/min',
      },
      {
        primaryColor: colors.primitives.yellow[300],
        data: requestsChartData,
        getValue: (stats: QueryHeatMapStackData) => ({
          timestamp: stats.timestamp,
          buckets: decodeSparseBuckets(
            stats.metrics.cachedHistogram.sparseBuckets,
          ),
        }),
        hasData: (stats: QueryHeatMapStackData) =>
          stats.metrics.cachedHistogram.sparseBuckets.some((v) => v > 0),
        label: 'Cache hits',
        unit: '/min',
      },
    ],
    [isSubscriptionOperation, requestsChartData],
  );

  const eventsHeatmapStacks = useMemo(
    (): MinLengthArray<2, HeatmapStackConfig<QueryHeatMapStackData>> => [
      {
        primaryColor: customColors.border.beta,
        data: eventsChartData,
        getValue: (stats: QueryHeatMapStackData) => ({
          timestamp: stats.timestamp,
          buckets: decodeSparseBuckets(
            stats.metrics.uncachedHistogram.sparseBuckets,
          ),
        }),
        hasData: (stats: QueryHeatMapStackData) =>
          stats.metrics.uncachedHistogram.sparseBuckets.some((v) => v > 0),
        label: 'Notifications',
        unit: '/min',
      },
      {
        primaryColor: colors.primitives.yellow[300],
        data: eventsChartData,
        getValue: (stats: QueryHeatMapStackData) => ({
          timestamp: stats.timestamp,
          buckets: decodeSparseBuckets(
            stats.metrics.cachedHistogram.sparseBuckets,
          ),
        }),
        hasData: (stats: QueryHeatMapStackData) =>
          stats.metrics.cachedHistogram.sparseBuckets.some((v) => v > 0),
        label: 'Cache hits',
        unit: '/min',
      },
    ],
    [eventsChartData],
  );

  const requestsHeatmapStackInfo = useStackInfo<
    QueryHeatMapStackData,
    { timestamp: Timestamp['output']; buckets: HistogramBucket[] },
    HeatmapStackConfig<QueryHeatMapStackData>
  >(requestsHeatmapStacks);

  const eventsHeatmapStackInfo = useStackInfo<
    QueryHeatMapStackData,
    { timestamp: Timestamp['output']; buckets: HistogramBucket[] },
    HeatmapStackConfig<QueryHeatMapStackData>
  >(eventsHeatmapStacks);

  return {
    requestsHeatmapStackInfo,
    eventsHeatmapStackInfo,
  };
}
